import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Typography, Tooltip,
  FormControlLabel, Switch
} from '@material-ui/core';
import { useNotify } from 'react-admin';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
} from '@material-ui/lab/Autocomplete';
import InfoIcon from '@material-ui/icons/Info';
import {
  Action,
  FunctionDefinition,
  Argument,
  RenderableComponent,
  DISPLAY_CONTEXT_ALL,
  DISPLAY_CONTEXT_OPTIONS,
  DisplayContext
} from './types';
import { PREDEFINED_CONDITIONS } from './definitions';
import { ConditionField } from './conditions/ConditionField';
import { buildDefaultArgs, validateAndNormalizeArgs } from './nba.utils';
import { makeStyles } from '@material-ui/core/styles';

interface EditActionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (action: Action) => void;
  action: Action | null;
  availableFunctions: FunctionDefinition[];
  availableComponents: RenderableComponent[];
}

const emptyAction: Omit<Action, 'id'> = {
  clientId: '',
  name: '',
  order: 0,
  description: '',
  motivation: '',
  displayContext: [DISPLAY_CONTEXT_ALL],
  conditions: {},
  content: [],
  onActionCallIdentifier: '',
  renderAnotherComponent: '',
  args: {},
};

const useStyles = makeStyles({
  sectionBox: {
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: 16,
    marginTop: 16,
  },
  inlineRow: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    marginLeft: 8,
    cursor: 'help',
  },
});

export const EditActionDialog: React.FC<EditActionDialogProps> = ({ 
  open, onClose, onSave, action, availableFunctions, availableComponents 
}) => {
  const [formData, setFormData] = useState<Omit<Action, 'id'>>(emptyAction);
  const classes = useStyles();
  const notify = useNotify();

  const selectedFunction = useMemo(() => {
    return availableFunctions.find(f => f.functionName === formData.onActionCallIdentifier);
  }, [formData.onActionCallIdentifier, availableFunctions]);

  useEffect(() => {
    if (action) {
      setFormData({ ...emptyAction, ...action });
    } else {
      setFormData(emptyAction);
    }
  }, [action, open]);

  useEffect(() => {
    if (!selectedFunction?.args?.length) {
      return;
    }

    setFormData(prev => {
      const prevArgs = prev.args || {};
      const nextArgs: Record<string, any> = { ...prevArgs };
      
      let changed = false;
      for (const arg of selectedFunction.args) {
        if (nextArgs[arg.name] === undefined && arg.default !== undefined) {
          nextArgs[arg.name] = arg.default;
          changed = true;
        }
      }

      return changed ? { ...prev, args: nextArgs } : prev;
    });
  }, [selectedFunction]);

  const handleIdentifierChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const newIdentifier = e.target.value as string;
    const nextFunction = availableFunctions.find(f => f.functionName === newIdentifier);
    const nextDefaultArgs = buildDefaultArgs(nextFunction);

    setFormData(prev => ({
      ...prev,
      onActionCallIdentifier: newIdentifier,
      args: nextDefaultArgs,
    }));
  };

  type DisplayContextOption = typeof DISPLAY_CONTEXT_OPTIONS[number];

  const handleDisplayContextChange = (
    _event: React.ChangeEvent<{}>,
    options: DisplayContextOption[],
    _reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<DisplayContextOption>,
  ) => {
    let nextOptions = options;

    if (details?.option) {
      const clickedValue = details.option.value as DisplayContext;
      const isAll = clickedValue === DISPLAY_CONTEXT_ALL;

      if (isAll) {
        nextOptions = options.filter(option => option.value === DISPLAY_CONTEXT_ALL);
      } else {
        nextOptions = options.filter(option => option.value !== DISPLAY_CONTEXT_ALL);
      }
    }

    const values = nextOptions.map(option => option.value as DisplayContext);
    const normalizedValues: DisplayContext[] = values.length ? values : [DISPLAY_CONTEXT_ALL];

    setFormData(prev => ({
      ...prev,
      displayContext: normalizedValues,
    }));
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArgChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, type } = e.target;
    let argValue: string | boolean;

    // Use a type guard to satisfy the compiler for different input types
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      argValue = e.target.checked;
    } else {
      argValue = e.target.value;
    }

    setFormData(prev => ({
      ...prev,
      args: {
        ...prev.args,
        [name]: argValue,
      },
    }));
  };

  const handleConditionChange = (conditionKey: string, value: any) => {
    setFormData(prev => {
      const { [conditionKey]: _omitted, ...rest } = prev.conditions || {};
      const nextConditions = value === undefined
        ? rest
        : { ...rest, [conditionKey]: value };
        
      return { ...prev, conditions: nextConditions };
    });
  };

  const handleSave = () => {
    if (!formData.name?.trim()) {
      notify('Action name cannot be empty.', 'error');
      return;
    }

    const result = validateAndNormalizeArgs(selectedFunction, formData.args);
    if (!result.ok) {
      notify(result.message, 'error');
      return;
    }

    onSave({
      ...formData,
      args: result.args,
      id: action?.id || '',
    });
    onClose();
  };

  const renderArgumentInput = (arg: Argument) => {
    switch (arg.type) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.args?.[arg.name]}
                color="primary"
                name={arg.name}
                onChange={handleArgChange}
              />
            }
            key={arg.name}
            label={`${arg.name} ${arg.required ? '*' : ''}`}
          />
        );
      case 'url':
        return (
          <div
            className={classes.inlineRow}
            key={arg.name}
          >
            <TextField
              fullWidth
              label={`${arg.name} ${arg.required ? '*' : ''}`}
              margin="normal"
              name={arg.name}
              value={formData.args?.[arg.name] || ''}
              variant="outlined"
              onChange={handleArgChange}
            />
            <Tooltip title={arg.description}>
              <InfoIcon
                className={classes.infoIcon}
                color="action"
              />
            </Tooltip>
          </div>
        );
      case 'string':
      default:
        return (
          <div
            className={classes.inlineRow}
            key={arg.name}
          >
            <TextField
              fullWidth
              multiline
              label={`${arg.name} ${arg.required ? '*' : ''}`}
              margin="normal"
              name={arg.name}
              value={formData.args?.[arg.name] || ''}
              variant="outlined"
              onChange={handleArgChange}
            />
            <Tooltip title={arg.description}>
              <InfoIcon
                className={classes.infoIcon}
                color="action"
              />
            </Tooltip>
          </div>
        );
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        {action ? 'Edit Action' : 'Create Action'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Action Name"
          margin="normal"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Order"
          margin="normal"
          name="order"
          type="number"
          value={formData.order}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          label="Description"
          margin="normal"
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          label="Motivation"
          margin="normal"
          name="motivation"
          value={formData.motivation || ''}
          onChange={handleChange}
        />

        <Autocomplete
          multiple
          getOptionLabel={(option: DisplayContextOption) => option.label}
          options={DISPLAY_CONTEXT_OPTIONS as unknown as DisplayContextOption[]}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Display Context"
              margin="normal"
              variant="standard"
            />
          )}
          value={
            DISPLAY_CONTEXT_OPTIONS.filter(option =>
              (formData.displayContext || [DISPLAY_CONTEXT_ALL]).includes(option.value)
            ) as DisplayContextOption[]
          }
          onChange={handleDisplayContextChange}
        />

        <FormControl
          fullWidth
          margin="normal"
        >
          <InputLabel>On Action Call Identifier</InputLabel>
          <Select
            name="onActionCallIdentifier"
            value={formData.onActionCallIdentifier}
            onChange={handleIdentifierChange}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableFunctions.map(func => (
              <MenuItem
                key={func._id}
                value={func.functionName}
              >
                {func.functionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedFunction && (
          <div className={classes.sectionBox}>
            <Typography
              gutterBottom
              variant="h6"
            >
              Function Arguments for:
              {selectedFunction.functionName}
            </Typography>
            {selectedFunction.args.length > 0 ? (
              selectedFunction.args.map(arg => renderArgumentInput(arg))
            ) : (
              <Typography color="textSecondary">This function requires no arguments.</Typography>
            )}
          </div>
        )}

        <FormControl
          fullWidth
          margin="normal"
        >
          <InputLabel>Render Another Component</InputLabel>
          <Select
            name="renderAnotherComponent"
            value={formData.renderAnotherComponent || ''}
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableComponents.map(comp => (
              <MenuItem
                key={comp._id}
                value={comp.componentName}
              >
                <Tooltip
                  placement="right"
                  title={comp.description}
                >
                  <span>
                    {comp.componentName}
                  </span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className={classes.sectionBox}>
          <Typography
            gutterBottom
            variant="h6"
          >
            Conditions
          </Typography>
          {PREDEFINED_CONDITIONS.map(def => (
            <ConditionField
              definition={def}
              key={def.key}
              value={formData.conditions?.[def.key]}
              onChange={(val: any) => handleConditionChange(def.key, val)}
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};